import type { UserData } from "./userData";

interface InitParametersResultError {
	type: "init-result-error";
	errorType:
		| "ERROR_REASON_MISSING_CLIENTID"
		| "ERROR_REASON_MISSING_SECRET"
		| "ERROR_REASON_INCOMPLETE_USBL_PARAMS";
	error: Error;
	missingUsblParams?: string[];
}

interface InitParametersResultInitOk {
	type: "init-result-parameters-ok";
	id: string;
	secret: string;
	viewMode: "widget" | "link" | "preview";
}

interface InitParametersResultUserAuthenticated {
	type: "init-result-user-authenticated";
	id: string;
	secret: string;
	viewMode: "widget" | "link";
	token: string;
	userData: UserData;
	selectedCompanyId: string;
}

interface InitParametersResultUsbl {
	type: "init-result-usbl";
	id: string;
	secret: string;
	viewMode: "widget" | "link";
	userData: UserData;
}

type InitParametersResult =
	| InitParametersResultError
	| InitParametersResultInitOk
	| InitParametersResultUserAuthenticated
	| InitParametersResultUsbl;

const getValuesFromSessionStorage = ():
	| InitParametersResultUserAuthenticated
	| undefined => {
	const id = sessionStorage.getItem("id");
	const secret = sessionStorage.getItem("secret");
	const token = sessionStorage.getItem("token");
	const phoneCountryCode = sessionStorage.getItem("phoneCountryCode");
	const phoneNumber = sessionStorage.getItem("phoneNumber");
	const phoneVerificationCode = sessionStorage.getItem("phoneVerificationCode");
	const selectedCompanyId = sessionStorage.getItem("selectedCompanyId");
	const viewMode =
		sessionStorage.getItem("viewMode") === "link" ? "link" : "widget";

	if (
		id &&
		secret &&
		token &&
		phoneNumber &&
		phoneVerificationCode &&
		phoneCountryCode &&
		viewMode &&
		selectedCompanyId
	) {
		return {
			type: "init-result-user-authenticated",
			id,
			secret,
			token,
			userData: { phoneCountryCode, phone: phoneNumber, phoneVerificationCode },
			viewMode,
			selectedCompanyId,
		};
	}
};

const USBL_PARAMS = [
	"phone",
	"phoneCountryCode",
	"phoneVerificationCode",
] as const;

export const getAppInitParameters = (): InitParametersResult => {
	const sessionValues = getValuesFromSessionStorage();

	const urlParams = new URLSearchParams(window.location.search);
	const idFromUrl = urlParams.get("id") ?? urlParams.get("clientid");
	const isPreviewFromUrl = urlParams.get("preview");
	const secretFromUrl = urlParams.get("secret");
	const viewModeFromUrl =
		urlParams.get("viewmode") === "link" ? "link" : "widget";

	const phoneFromUrl = urlParams.get("phone");
	const phoneCountryCodeFromUrl = urlParams.get("phoneCountryCode");
	const phoneVerificationCodeFromUrl = urlParams.get("phoneVerificationCode");

	const usblParamValues = {
		phone: phoneFromUrl,
		phoneCountryCode: phoneCountryCodeFromUrl,
		phoneVerificationCode: phoneVerificationCodeFromUrl,
	};
	const presentUsblParams = USBL_PARAMS.filter((p) =>
		Boolean(usblParamValues[p]),
	);
	const isPartialUsbl =
		presentUsblParams.length > 0 &&
		presentUsblParams.length < USBL_PARAMS.length;
	const isFullUsbl = presentUsblParams.length === USBL_PARAMS.length;

	if (idFromUrl || secretFromUrl || !sessionValues) {
		if (!idFromUrl) {
			return {
				type: "init-result-error",
				errorType: "ERROR_REASON_MISSING_CLIENTID",
				error: new Error(`expected id=${idFromUrl} from to have a valid value`),
			};
		}

		if (!secretFromUrl) {
			return {
				type: "init-result-error",
				errorType: "ERROR_REASON_MISSING_SECRET",
				error: new Error(
					`expected secret=${secretFromUrl} from to have a valid value`,
				),
			};
		}

		if (isPartialUsbl) {
			const missingUsblParams = USBL_PARAMS.filter((p) => !usblParamValues[p]);
			return {
				type: "init-result-error",
				errorType: "ERROR_REASON_INCOMPLETE_USBL_PARAMS",
				error: new Error(
					`incomplete USBL params: expected ${USBL_PARAMS.join(", ")}, missing ${missingUsblParams.join(", ")}`,
				),
				missingUsblParams,
			};
		}

		if (isFullUsbl) {
			return {
				type: "init-result-usbl",
				id: idFromUrl,
				secret: secretFromUrl,
				viewMode: viewModeFromUrl,
				userData: {
					phone: phoneFromUrl as string,
					phoneCountryCode: phoneCountryCodeFromUrl as string,
					phoneVerificationCode: phoneVerificationCodeFromUrl as string,
				},
			};
		}

		const viewMode = isPreviewFromUrl ? "preview" : viewModeFromUrl;

		return {
			type: "init-result-parameters-ok",
			id: idFromUrl,
			secret: secretFromUrl,
			viewMode,
		};
	}
	return sessionValues;
};

export const saveInitParamters = (
	values: InitParametersResultUserAuthenticated,
): void => {
	sessionStorage.setItem("id", values.id);
	sessionStorage.setItem("secret", values.secret);
	sessionStorage.setItem("token", values.token);
	sessionStorage.setItem("viewMode", values.viewMode);
	sessionStorage.setItem("phoneNumber", values.userData.phone);
	sessionStorage.setItem("phoneCountryCode", values.userData.phoneCountryCode);
	sessionStorage.setItem(
		"phoneVerificationCode",
		values.userData.phoneVerificationCode,
	);
	sessionStorage.setItem("selectedCompanyId", values.selectedCompanyId);
};

export const clearInitParameters = () => {
	sessionStorage.clear();
};
